import React, { useState, useRef, useEffect } from "react";
import { useMutation, gql } from "@apollo/client";

const QUICK_PASS_LOGIN = gql`
  mutation QuickPassLogin($quickPass: String!) {
    quickPassLogin(quickPass: $quickPass) {
      token
      user {
        id
        name
        lastname
        subuser_id
        privileges
        partner {
          id
          status
          company_name
          owner_name
          owner_lastname
          logo
          branches
        }
        branch {
          id
          name
        }
      }
      settings {
        id
        type
        pref_key
        pref_value
        is_global
      }
    }
  }
`;

type LockScreenProps = {
  onUnlock: () => void;
};

const LockScreen: React.FC<LockScreenProps> = ({ onUnlock }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [code, setCode] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [quickPassLogin, { loading }] = useMutation(QUICK_PASS_LOGIN);

  const logoUrl = user?.partner?.logo
    ? user.partner.logo.startsWith("http")
      ? user.partner.logo
      : `https://cdn.foopos.mx${user.partner.logo?.replace("/img", "").slice(0)}`
    : "https://fooapp.mx/img/foo_mascot_chef_hat@2x.svg";

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError("");
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
    // Auto-submit when all 4 digits entered
    if (value && index === 3 && newCode.every((d) => d !== "")) {
      handleUnlock(newCode.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleUnlock = async (quickPass?: string) => {
    const pass = quickPass || code.join("");
    if (pass.length !== 4) {
      setError("Ingresa el código de 4 dígitos");
      return;
    }
    try {
      const { data } = await quickPassLogin({ variables: { quickPass: pass } });
      if (data?.quickPassLogin) {
        const { token, user: newUser, settings: rawSettings } = data.quickPassLogin;
        const currentSubuserId = user.subuser_id;
        // Parse settings same as login page
        const settings_obj: Record<string, any> = {};
        if (rawSettings) {
          Object.keys(rawSettings).forEach((key) => {
            const setting = { ...rawSettings[key] };
            const pref_key = setting.pref_key;
            if (setting.pref_value === "true" || setting.pref_value === "false") {
              setting.pref_value = setting.pref_value === "true";
            } else if (!isNaN(setting.pref_value)) {
              setting.pref_value = parseInt(setting.pref_value);
            }
            settings_obj[pref_key] = setting.pref_value;
          });
        }
        localStorage.setItem("user", JSON.stringify(newUser));
        localStorage.setItem("settings", JSON.stringify(settings_obj));
        localStorage.setItem("token", token);
        // If different user logged in, reload to refresh everything
        if (newUser.subuser_id !== currentSubuserId) {
          localStorage.removeItem("locked");
          window.location.reload();
        } else {
          onUnlock();
        }
      }
    } catch (err: any) {
      setError(err?.message?.replace("GraphQL error: ", "") || "Código incorrecto");
      setCode(["", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("tables");
    localStorage.removeItem("all_orders");
    localStorage.removeItem("menu_all");
    localStorage.removeItem("settings");
    localStorage.removeItem("token");
    localStorage.removeItem("locked");
    window.location.href = "/login";
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "#292929",
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        src={logoUrl}
        alt="Logo"
        style={{
          width: 120,
          height: 120,
          borderRadius: "50%",
          objectFit: "cover",
          marginBottom: 16,
          background: "#fff",
          padding: 8,
        }}
      />
      <p style={{ color: "#aaa", fontSize: 14, marginBottom: 4 }}>
        {user?.partner?.company_name}
      </p>
      <p style={{ color: "#fff", fontSize: 16, marginBottom: 24 }}>
        <i className="fas fa-lock" style={{ marginRight: 8 }} />
        Pantalla bloqueada
      </p>

      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        {code.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="password"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={loading}
            style={{
              width: 56,
              height: 64,
              textAlign: "center",
              fontSize: 28,
              borderRadius: 10,
              border: error ? "2px solid #FF6028" : "2px solid #555",
              background: "#3a3a3a",
              color: "#fff",
              outline: "none",
            }}
          />
        ))}
      </div>
      {error && (
        <p style={{ color: "#FF6028", fontSize: 14, marginBottom: 12 }}>
          {error}
        </p>
      )}
      <button
        className="btn btn-warning"
        onClick={() => handleUnlock()}
        disabled={loading || code.some((d) => !d)}
        style={{ minWidth: 200, fontWeight: 600, marginBottom: 24 }}
      >
        {loading ? (
          <span>
            <i className="fas fa-spinner fa-spin" /> Verificando...
          </span>
        ) : (
          <span>
            <i className="fas fa-unlock" style={{ marginRight: 8 }} />
            Desbloquear
          </span>
        )}
      </button>
      <button
        className="btn btn-link"
        onClick={handleLogout}
        style={{ color: "#888", fontSize: 13 }}
      >
        <i className="fas fa-sign-out-alt" style={{ marginRight: 6 }} />
        Cerrar Sesión
      </button>
    </div>
  );
};

export default LockScreen;
